import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { CustomerShell } from "@/components/CustomerShell";
import { CareShell } from "@/components/CareShell";
import { useMode } from "@/context/ModeContext";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Gift, Copy, Share2, Loader2, Building2, Check, Users, CalendarCheck, IndianRupee, ChevronLeft, Trophy } from "lucide-react";
import { ReferralTracker } from "@/components/ReferralTracker";

const RSC = {
    QUALIFIED: "bg-amber-50 text-amber-700", APPROVED: "bg-blue-50 text-blue-700",
    PAID: "bg-emerald-50 text-emerald-700", REJECTED: "bg-red-50 text-red-600",
    UNDER_REVIEW: "bg-slate-100 text-slate-600", REVERSED: "bg-red-50 text-red-600",
};

export default function Referral() {
    const nav = useNavigate();
    const { mode } = useMode();
    const care = mode === "care";
    const accent = care ? "#F43F5E" : "#009C95";
    const Shell = care ? CareShell : CustomerShell;
    const [data, setData] = useState(null);
    const [board, setBoard] = useState(null);
    const [showBank, setShowBank] = useState(false);
    const load = () => api.get("/referral/me").then((r) => setData(r.data)).catch(() => setData(null));
    useEffect(() => { load(); api.get("/referral/leaderboard").then((r) => setBoard(r.data)).catch(() => {}); }, []);

    if (!data) return <Shell><div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-7 w-7 animate-spin" style={{ color: accent }} /></div></Shell>;

    const link = `${window.location.origin}/register?ref=${data.code}`;
    const share = `Join HOMIIE — Your Home, Managed! Book trusted home services & elder care. Use my code ${data.code} or this link: ${link}`;
    const copy = (txt, msg) => { navigator.clipboard.writeText(txt); toast.success(msg); };
    const doShare = async () => {
        if (navigator.share) { try { await navigator.share({ title: "HOMIIE", text: share, url: link }); } catch (e) {} }
        else copy(share, "Invite copied — paste it anywhere");
    };

    return (
        <Shell>
            <div className="rounded-b-3xl px-5 pb-8 pt-6 text-white" style={{ backgroundColor: care ? accent : "#09233B" }}>
                <button data-testid="referral-back" onClick={() => nav(-1)} className="mb-3 flex items-center gap-1 text-sm text-white/70"><ChevronLeft size={16} /> Back</button>
                <div className="flex items-center gap-2"><Gift size={22} className={care ? "text-white" : "text-saffron"} /><h1 className="font-heading text-2xl font-extrabold">Refer & Earn ₹{data.reward_amount}</h1></div>
                <p className="mt-1 text-sm text-white/65">Invite friends & family. You earn ₹{data.reward_amount} in cash to your bank for every new customer who completes their first booking.</p>
            </div>

            <div className="px-5 py-5">
                {/* code + QR */}
                <div className="rounded-3xl border border-navy/10 bg-white p-5 text-center">
                    <p className="text-xs font-semibold uppercase tracking-wide text-navy/45">Your referral code</p>
                    <p data-testid="referral-code" className="mt-1 font-heading text-3xl font-extrabold tracking-wider text-navy">{data.code}</p>
                    <div className="mt-4 flex justify-center rounded-2xl bg-white p-3"><QRCodeSVG data-testid="referral-qr" value={link} size={148} fgColor={accent} level="M" /></div>
                    <div className="mt-4 flex gap-2">
                        <button data-testid="referral-copy-code" onClick={() => copy(data.code, "Code copied")} className="flex flex-1 items-center justify-center gap-1.5 rounded-full border border-navy/15 py-2.5 text-sm font-bold text-navy"><Copy size={15} /> Copy code</button>
                        <button data-testid="referral-share" onClick={doShare} className="flex flex-1 items-center justify-center gap-1.5 rounded-full py-2.5 text-sm font-bold text-white" style={{ backgroundColor: accent }}><Share2 size={15} /> Share invite</button>
                    </div>
                </div>

                {/* stats */}
                <div className="mt-4 grid grid-cols-3 gap-2.5">
                    <Stat icon={Users} label="Invited" value={data.invited} accent={accent} />
                    <Stat icon={CalendarCheck} label="Booked" value={data.booked} accent={accent} />
                    <Stat icon={IndianRupee} label="Paid out" value={`₹${data.paid_total}`} accent={accent} />
                </div>
                {data.pending_total > 0 && <p className="mt-2 text-center text-xs font-semibold text-amber-600">₹{data.pending_total} in rewards pending approval/payout</p>}

                {/* society leaderboard */}
                {board && board.leaderboard?.length > 0 && (
                    <div className="mt-5 rounded-3xl border border-navy/10 bg-white p-5" data-testid="referral-leaderboard">
                        <div className="flex items-center gap-2"><Trophy size={18} style={{ color: accent }} /><h3 className="font-heading text-base font-extrabold text-navy">Society leaderboard</h3></div>
                        <p className="text-xs text-navy/50">Top referring communities{board.my_society ? ` · you're in ${board.my_society}` : ""}</p>
                        <div className="mt-3 space-y-1.5">
                            {board.leaderboard.map((row) => {
                                const mine = row.society === board.my_society;
                                const medal = row.rank === 1 ? "🥇" : row.rank === 2 ? "🥈" : row.rank === 3 ? "🥉" : `#${row.rank}`;
                                return (
                                    <div key={row.society} data-testid={`leaderboard-row-${row.rank}`} className="flex items-center gap-3 rounded-xl px-3 py-2.5" style={mine ? { backgroundColor: `${accent}0d`, border: `1px solid ${accent}55` } : { backgroundColor: "rgba(9,35,59,0.02)" }}>
                                        <span className="w-7 text-center text-sm font-extrabold text-navy">{medal}</span>
                                        <div className="flex-1 overflow-hidden"><p className="truncate font-heading text-sm font-bold text-navy">{row.society}{mine && <span className="ml-1 text-[10px] font-bold" style={{ color: accent }}>• you</span>}</p><p className="text-[11px] text-navy/45">{row.referrers} referrer{row.referrers > 1 ? "s" : ""}</p></div>
                                        <div className="text-right"><p className="font-heading text-sm font-extrabold text-navy">{row.referrals}</p><p className="text-[10px] text-navy/45">referrals</p></div>
                                    </div>
                                );
                            })}
                        </div>
                        <p className="mt-3 text-[11px] text-navy/40">Get your society to the top — every friend who completes a first booking counts.</p>
                    </div>
                )}

                {/* bank */}
                <button data-testid="referral-bank-btn" onClick={() => setShowBank(true)} className="mt-4 flex w-full items-center gap-3 rounded-2xl border border-navy/10 bg-white p-4 text-left">
                    <div className="rounded-xl p-2.5" style={{ backgroundColor: `${accent}1a` }}><Building2 size={20} style={{ color: accent }} /></div>
                    <div className="flex-1">
                        <h3 className="font-heading font-bold text-navy">Payout bank account</h3>
                        <p className="text-xs text-navy/50">{data.bank ? `${data.bank.account_name} · ${data.bank.account_number_masked} · ${data.bank.ifsc}` : "Add your bank to receive cash rewards"}</p>
                    </div>
                    {data.bank ? <Check size={18} className="text-emerald-500" /> : <span className="rounded-full bg-saffron/15 px-2 py-1 text-[11px] font-bold text-saffron">Add</span>}
                </button>

                <ReferralTracker />
                <p className="mt-4 text-center text-[11px] text-navy/35">Rewards are paid after the referred customer's first booking is completed & paid, and pass fraud checks. Payouts go to your verified bank account.</p>
            </div>

            {showBank && <BankSheet current={data.bank} onClose={() => setShowBank(false)} onSaved={() => { setShowBank(false); load(); }} />}
        </Shell>
    );
}

function Stat({ icon: Icon, label, value, accent }) {
    return <div className="rounded-2xl border border-navy/10 bg-white p-3 text-center"><Icon size={16} className="mx-auto" style={{ color: accent }} /><p className="mt-1 font-heading text-lg font-extrabold text-navy">{value}</p><p className="text-[11px] text-navy/50">{label}</p></div>;
}

export function BankSheet({ current, onClose, onSaved }) {
    const [f, setF] = useState({ account_name: current?.account_name || "", account_number: "", ifsc: current?.ifsc || "" });
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
    const save = async () => {
        setBusy(true);
        try { await api.post("/referral/bank", f); toast.success("Bank details saved"); onSaved(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-navy/50" onClick={onClose}>
            <div className="w-full max-w-md rounded-t-3xl bg-white p-6 pb-8" onClick={(e) => e.stopPropagation()} data-testid="bank-sheet">
                <h3 className="font-heading text-lg font-extrabold text-navy">Payout bank account</h3>
                <p className="mb-4 text-sm text-navy/55">Approved referral rewards are transferred here. The account must be in your own name, matching your HOMIIE profile.</p>
                <div className="space-y-3">
                    <Field label="Account holder name" testid="bank-name" value={f.account_name} onChange={(v) => set("account_name", v)} />
                    <Field label="Account number" testid="bank-number" value={f.account_number} onChange={(v) => set("account_number", v.replace(/\D/g, ""))} ph={current ? "Re-enter to update" : ""} />
                    <Field label="IFSC code" testid="bank-ifsc" value={f.ifsc} onChange={(v) => set("ifsc", v.toUpperCase())} />
                </div>
                <button data-testid="bank-save-btn" disabled={busy} onClick={save} className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-teal py-3.5 text-sm font-bold text-white disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} SAVE BANK DETAILS</button>
            </div>
        </div>
    );
}

function Field({ label, testid, value, onChange, ph }) {
    return <div><label className="mb-1 block text-xs font-semibold text-navy/60">{label}</label><input data-testid={testid} value={value} placeholder={ph} onChange={(e) => onChange(e.target.value)} className="w-full rounded-xl border border-navy/15 px-3 py-3 text-sm outline-none focus:border-teal" /></div>;
}
